/**
 * Promise Time: pure helpers that render the server promise.
 *
 * Clients never compute an ETA (contracts section 5). Everything here formats
 * or selects values the server already decided. The branch shadow switch
 * (BranchConfig.visibleToCustomers / QuoteEtaResponse.visibleToCustomers)
 * gates every customer-facing use.
 */
import { OrderStatusEnum } from "@/api/online-ordering-service/model";
import type {
  OrderOutput,
  OrderPromise,
  PromiseWindow,
  QuoteEtaResponse,
} from "@/api/online-ordering-service/model";

export type TFn = (key: string, options?: any) => string;

const DONE_STATUSES: ReadonlySet<OrderStatusEnum> = new Set([
  OrderStatusEnum.Delivered,
  OrderStatusEnum.Completed,
  OrderStatusEnum.Cancelled,
  OrderStatusEnum.Rejected,
]);

/** 24-hour wall-clock time ("19:40"), the format the promise copy uses. */
export function formatClock(iso: string, locale?: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const fmt: Intl.DateTimeFormatOptions = { hour: "2-digit", minute: "2-digit", hour12: false };
  try {
    return d.toLocaleTimeString(locale || "en-GB", fmt);
  } catch {
    return d.toLocaleTimeString("en-GB", fmt);
  }
}

export function formatWindow(
  window: PromiseWindow | null | undefined,
  locale?: string,
): { from: string; to: string } | null {
  if (!window) return null;
  return { from: formatClock(window.earliest, locale), to: formatClock(window.latest, locale) };
}

/** The window the customer is promised: arrival for delivery, ready otherwise. */
export function publicWindow(promise: OrderPromise): PromiseWindow {
  if (promise.kind === "delivery" && promise.current.arriveAt) return promise.current.arriveAt;
  return promise.current.readyAt;
}

/** The order's promise when this branch shows promises to customers, else null. */
export function visiblePromise(
  order: { promise?: OrderPromise | null } | null | undefined,
  visible: boolean | null | undefined,
): OrderPromise | null {
  if (visible !== true) return null;
  const promise = order?.promise ?? null;
  return promise?.current ? promise : null;
}

/** Checkout copy for a quote: "Arrives 19:40 to 19:50" or "Ready in 15 to 25 min". */
export function quoteLabel(
  quote: QuoteEtaResponse | null | undefined,
  t: TFn,
  opts: { now?: Date; locale?: string } = {},
): string | null {
  if (!quote || quote.visibleToCustomers !== true || !quote.promise?.current) return null;
  const promise = quote.promise;
  if (promise.kind === "delivery" && promise.current.arriveAt) {
    const w = formatWindow(promise.current.arriveAt, opts.locale)!;
    return t("v2.checkout.quote_arrives", { from: w.from, to: w.to, defaultValue: `Arrives ${w.from} to ${w.to}` });
  }
  if (promise.current.source === "scheduled") {
    const w = formatWindow(promise.current.readyAt, opts.locale)!;
    return t("v2.checkout.quote_ready_at", { from: w.from, to: w.to, defaultValue: `Ready ${w.from} to ${w.to}` });
  }
  const now = (opts.now ?? new Date()).getTime();
  const min = Math.max(0, Math.ceil((new Date(promise.current.readyAt.earliest).getTime() - now) / 60_000));
  const max = Math.max(min, Math.ceil((new Date(promise.current.readyAt.latest).getTime() - now) / 60_000));
  return t("v2.checkout.quote_ready_in", { min, max, defaultValue: `Ready in ${min} to ${max} min` });
}

/**
 * "Has your order arrived?" is asked once the promised arrival window has
 * passed on a visible delivery promise, until the customer answers or the
 * order reaches a terminal status. Zero operator cost (spec section 3.5).
 */
export function isArrivalPromptDue(
  order: OrderOutput | null | undefined,
  visible: boolean | null | undefined,
  now: Date,
): boolean {
  const promise = visiblePromise(order, visible);
  if (!order || !promise || promise.kind !== "delivery" || !promise.current.arriveAt) return false;
  if (order.customerConfirmedArrivalAt) return false;
  if (order.status && DONE_STATUSES.has(order.status)) return false;
  return now.getTime() > new Date(promise.current.arriveAt.latest).getTime();
}
